import React, { useContext, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import { ToastContext } from "../../../../App";
import {
  useLazyGetDiningOptionListQuery,
  useUpdateDiningOptionMutation,
} from "../../../../redux/services/diningOption";
import getSubdomain from "../../../../utils/getSubdomain";

import * as styles from "../DiningOptions.module.css";

const LIST_PATH = "/app/manager/dining-options";

function DiningOptionEditPage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const stationId = searchParams.get("station");

  const [
    fetchDiningOptions,
    { data: diningOptions },
  ] = useLazyGetDiningOptionListQuery();

  useEffect(() => {
    if (stationId) {
      fetchDiningOptions({ id: stationId, subDomain: getSubdomain() });
    }
  }, [stationId, fetchDiningOptions]);

  const diningOption = useMemo(
    () => diningOptions?.find((opt) => String(opt.id) === String(id)),
    [diningOptions, id],
  );

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { title: "", active: "Active" },
  });

  useEffect(() => {
    if (diningOption) {
      reset({
        title: diningOption.name || "",
        active: diningOption.active ? "Active" : "Inactive",
      });
    }
  }, [diningOption, reset]);

  const [
    updateDiningOption,
    {
      isSuccess: isUpdateDiningOptionSuccess,
      isError: isUpdateDiningOptionError,
      error: updateDiningOptionError,
    },
  ] = useUpdateDiningOptionMutation();

  useEffect(() => {
    if (isUpdateDiningOptionSuccess) {
      showToast("success", "Dining option", "Dining option updated!");
      navigate(LIST_PATH);
    }
  }, [isUpdateDiningOptionSuccess]);

  useEffect(() => {
    if (isUpdateDiningOptionError) {
      showToast(
        "error",
        updateDiningOptionError?.status,
        updateDiningOptionError?.data?.message ||
          (updateDiningOptionError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isUpdateDiningOptionError]);

  const onSaveEdit = (data) => {
    if (!stationId) {
      showToast("warn", "Warning", "Station is missing");
      return;
    }
    updateDiningOption({
      id,
      stationId: Number(stationId),
      subdomain: getSubdomain(),
      data: {
        name: data.title,
        active: data.active === "Active",
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(onSaveEdit)();
    }
  };

  const title = diningOption?.name
    ? `Edit Dining Option — ${diningOption.name}`
    : "Edit Dining Option";

  return (
    <EntityFormPage
      title={title}
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onSaveEdit)}
        />
      }
    >
      <form onSubmit={handleSubmit(onSaveEdit)}>
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Title</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                onKeyDown={handleKeyDown}
                placeholder="Title"
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <SelectButton
              {...field}
              options={["Active", "Inactive"]}
              allowEmpty={false}
              className={`${styles.selectButton}`}
            />
          )}
        />

        <button type="submit" hidden />
      </form>
    </EntityFormPage>
  );
}

export default DiningOptionEditPage;
